import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  Plus,
  Settings,
  Trash2,
  Check,
  CalendarPlus,
  CalendarX,
  Loader2,
  Clock,
  Sun,
  AlertCircle,
  Star,
  Edit2,
  Save,
  X,
  Upload,
  FileSpreadsheet,
  Download,
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';
import { format, parseISO, parse, isValid } from 'date-fns';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', shortLabel: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
  { key: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
  { key: 'friday', label: 'Friday', shortLabel: 'Fri' },
  { key: 'saturday', label: 'Saturday', shortLabel: 'Sat' },
  { key: 'sunday', label: 'Sunday', shortLabel: 'Sun' },
];

const CalendarsPage = () => {
  const { activeProject, can } = useProject();

  // State
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [calendarDetails, setCalendarDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Create calendar dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCalendar, setNewCalendar] = useState({
    name: '',
    description: '',
    is_default: false,
    hours_per_day: 8,
    hours_per_week: 40,
    working_days: DAYS_OF_WEEK.map(day => ({
      day: day.key,
      is_working: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day.key),
      work_start: '08:00',
      work_end: '17:00',
      hours_per_day: 8.0,
    })),
    holidays: [],
  });

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Add holiday dialog state
  const [showAddHolidayDialog, setShowAddHolidayDialog] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    name: '',
    is_working: false,
  });
  const [isAddingHoliday, setIsAddingHoliday] = useState(false);

  // Import holidays dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Fetch calendars
  const fetchCalendars = useCallback(async () => {
    if (!activeProject?.id) return;

    setLoading(true);
    try {
      const response = await api.get(`/calendars/project/${activeProject.id}`);
      setCalendars(response.data);

      // Auto-select default calendar if none selected
      if (!selectedCalendar && response.data.length > 0) {
        const defaultCal = response.data.find(c => c.is_default) || response.data[0];
        setSelectedCalendar(defaultCal.id);
      }
    } catch (error) {
      console.error('Failed to fetch calendars:', error);
      // If no calendars exist, try to fetch/create default
      if (error.response?.status === 404 || calendars.length === 0) {
        try {
          const defaultResponse = await api.get(`/calendars/project/${activeProject.id}/default`);
          setCalendars([{
            id: defaultResponse.data.id,
            name: defaultResponse.data.name,
            is_default: defaultResponse.data.is_default,
            working_days_count: defaultResponse.data.working_days?.filter(d => d.is_working).length || 5,
            holidays_count: defaultResponse.data.holidays?.length || 0,
          }]);
          setSelectedCalendar(defaultResponse.data.id);
        } catch (defError) {
          console.error('Failed to get default calendar:', defError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [activeProject?.id]);

  // Fetch calendar details
  const fetchCalendarDetails = useCallback(async () => {
    if (!selectedCalendar) return;

    setLoadingDetails(true);
    try {
      const response = await api.get(`/calendars/${selectedCalendar}`);
      setCalendarDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar details:', error);
      toast.error('Failed to load calendar details');
    } finally {
      setLoadingDetails(false);
    }
  }, [selectedCalendar]);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  useEffect(() => {
    if (selectedCalendar) {
      fetchCalendarDetails();
    }
  }, [selectedCalendar, fetchCalendarDetails]);

  // Create calendar
  const handleCreateCalendar = async () => {
    if (!newCalendar.name.trim()) {
      toast.error('Calendar name is required');
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        ...newCalendar,
        project_id: activeProject.id,
      };

      await api.post('/calendars', payload);
      toast.success('Calendar created successfully');
      setShowCreateDialog(false);
      setNewCalendar({
        name: '',
        description: '',
        is_default: false,
        hours_per_day: 8,
        hours_per_week: 40,
        working_days: DAYS_OF_WEEK.map(day => ({
          day: day.key,
          is_working: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day.key),
          work_start: '08:00',
          work_end: '17:00',
          hours_per_day: 8.0,
        })),
        holidays: [],
      });
      await fetchCalendars();
    } catch (error) {
      console.error('Failed to create calendar:', error);
      toast.error(apiErrorMessage(error, 'Failed to create calendar'));
    } finally {
      setIsCreating(false);
    }
  };

  // Start editing
  const handleStartEdit = () => {
    if (!calendarDetails) return;
    setEditingCalendar({
      name: calendarDetails.name,
      description: calendarDetails.description || '',
      is_default: calendarDetails.is_default,
      hours_per_day: calendarDetails.hours_per_day,
      hours_per_week: calendarDetails.hours_per_week,
      working_days: calendarDetails.working_days || [],
    });
    setIsEditing(true);
  };

  // Save changes
  const handleSaveChanges = async () => {
    if (!editingCalendar || !selectedCalendar) return;

    setIsSaving(true);
    try {
      await api.patch(`/calendars/${selectedCalendar}`, editingCalendar);
      toast.success('Calendar updated successfully');
      setIsEditing(false);
      setEditingCalendar(null);
      await fetchCalendarDetails();
      await fetchCalendars();
    } catch (error) {
      console.error('Failed to update calendar:', error);
      toast.error(apiErrorMessage(error, 'Failed to update calendar'));
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCalendar(null);
  };

  // Update working day in edit mode
  const updateWorkingDay = (dayKey, field, value) => {
    if (!editingCalendar) return;

    setEditingCalendar(prev => ({
      ...prev,
      working_days: prev.working_days.map(wd =>
        wd.day === dayKey ? { ...wd, [field]: value } : wd
      ),
    }));
  };

  // Set as default
  const handleSetDefault = async () => {
    if (!selectedCalendar) return;

    try {
      await api.patch(`/calendars/${selectedCalendar}`, { is_default: true });
      toast.success('Calendar set as default');
      await fetchCalendars();
      await fetchCalendarDetails();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set calendar as default');
    }
  };

  // Delete calendar
  const handleDeleteCalendar = async () => {
    if (!selectedCalendar) return;

    if (calendarDetails?.is_default) {
      toast.error('Cannot delete the default calendar');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this calendar?')) return;

    try {
      await api.delete(`/calendars/${selectedCalendar}`);
      toast.success('Calendar deleted');
      setSelectedCalendar(null);
      setCalendarDetails(null);
      await fetchCalendars();
    } catch (error) {
      console.error('Failed to delete calendar:', error);
      toast.error(apiErrorMessage(error, 'Failed to delete calendar'));
    }
  };

  // Add holiday
  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name.trim()) {
      toast.error('Date and name are required');
      return;
    }

    setIsAddingHoliday(true);
    try {
      await api.post(`/calendars/${selectedCalendar}/holidays`, newHoliday);
      toast.success('Holiday added');
      setShowAddHolidayDialog(false);
      setNewHoliday({ date: '', name: '', is_working: false });
      await fetchCalendarDetails();
    } catch (error) {
      console.error('Failed to add holiday:', error);
      toast.error(apiErrorMessage(error, 'Failed to add holiday'));
    } finally {
      setIsAddingHoliday(false);
    }
  };

  // Remove holiday
  const handleRemoveHoliday = async (holidayDate) => {
    try {
      await api.delete(`/calendars/${selectedCalendar}/holidays/${holidayDate}`);
      toast.success('Holiday removed');
      await fetchCalendarDetails();
    } catch (error) {
      console.error('Failed to remove holiday:', error);
      toast.error('Failed to remove holiday');
    }
  };

  // Handle file selection for import
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportError(null);
    setImportPreview([]);

    // Parse the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        const lines = content.split('\n').filter(line => line.trim());

        // Skip header row
        const dataLines = lines.slice(1);
        const holidays = [];

        for (const line of dataLines) {
          // Handle both CSV and tab-separated (Excel copy)
          const parts = line.includes('\t') ? line.split('\t') : line.split(',');

          if (parts.length >= 2) {
            const dateStr = parts[0].trim().replace(/"/g, '');
            const name = parts[1].trim().replace(/"/g, '');

            // Try to parse the date
            let parsedDate = null;

            // Try different date formats
            const formats = ['yyyy-MM-dd', 'MM/dd/yyyy', 'dd/MM/yyyy', 'M/d/yyyy'];
            for (const fmt of formats) {
              const attempt = parse(dateStr, fmt, new Date());
              if (isValid(attempt)) {
                parsedDate = format(attempt, 'yyyy-MM-dd');
                break;
              }
            }

            if (parsedDate && name) {
              holidays.push({
                date: parsedDate,
                name: name,
                is_working: false
              });
            }
          }
        }

        if (holidays.length === 0) {
          setImportError('No valid holidays found. Please ensure the file has columns: Date, Name');
        } else {
          setImportPreview(holidays);
        }
      } catch (err) {
        console.error('Error parsing file:', err);
        setImportError('Failed to parse file. Please ensure it is a valid CSV file.');
      }
    };

    reader.readAsText(file);
  };

  // Import holidays
  const handleImportHolidays = async () => {
    if (importPreview.length === 0 || !selectedCalendar) return;

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const holiday of importPreview) {
        try {
          await api.post(`/calendars/${selectedCalendar}/holidays`, holiday);
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to import holiday ${holiday.name}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} holiday${successCount > 1 ? 's' : ''}`);
        await fetchCalendarDetails();
      }

      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} holiday${errorCount > 1 ? 's' : ''} (may be duplicates)`);
      }

      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview([]);
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import holidays');
    } finally {
      setIsImporting(false);
    }
  };

  // Download sample CSV
  const handleDownloadSample = () => {
    const sampleCsv = `Date,Name
2025-01-01,New Year's Day
2025-01-20,Martin Luther King Jr. Day
2025-02-17,Presidents' Day
2025-05-26,Memorial Day
2025-07-04,Independence Day
2025-09-01,Labor Day
2025-10-13,Columbus Day
2025-11-11,Veterans Day
2025-11-27,Thanksgiving Day
2025-12-25,Christmas Day`;

    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holidays_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // No project selected
  if (!activeProject) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Calendar className="h-16 w-16 text-slate-200 mb-4" />
        <h2 className="text-lg font-semibold text-slate-700 mb-2">No Project Selected</h2>
        <p className="text-sm text-slate-500 max-w-md text-center">
          Select a project from the header to manage project calendars.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendars-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Project Calendars</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Define working days, hours, and holidays for {activeProject.name}
          </p>
        </div>
        {can(Action.MANAGE_SCHEDULES) && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-calendar-btn">
                <Plus className="h-4 w-4" />
                New Calendar
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col !p-0 gap-0">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="flex items-center gap-2">
                  <CalendarPlus className="h-5 w-5 text-blue-500" />
                  Create Calendar
                </DialogTitle>
                <DialogDescription>
                  Define a new calendar for scheduling calculations
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 px-6 py-2 overflow-y-auto">
                <div className="space-y-2">
                  <Label htmlFor="cal-name">Calendar Name *</Label>
                  <Input
                    id="cal-name"
                    placeholder="e.g., Standard 5-Day Week"
                    value={newCalendar.name}
                    onChange={(e) => setNewCalendar({ ...newCalendar, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cal-desc">Description</Label>
                  <Input
                    id="cal-desc"
                    placeholder="Optional description"
                    value={newCalendar.description}
                    onChange={(e) => setNewCalendar({ ...newCalendar, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hours per Day</Label>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      value={newCalendar.hours_per_day}
                      onChange={(e) => setNewCalendar({ ...newCalendar, hours_per_day: parseFloat(e.target.value) || 8 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hours per Week</Label>
                    <Input
                      type="number"
                      min="1"
                      max="168"
                      value={newCalendar.hours_per_week}
                      onChange={(e) => setNewCalendar({ ...newCalendar, hours_per_week: parseFloat(e.target.value) || 40 })}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Checkbox
                    id="cal-default"
                    checked={newCalendar.is_default}
                    onCheckedChange={(checked) => setNewCalendar({ ...newCalendar, is_default: checked })}
                  />
                  <Label htmlFor="cal-default" className="text-sm">Set as default calendar</Label>
                </div>

                {/* Working Days */}
                <div className="space-y-2 pt-2">
                  <Label className="text-sm font-medium">Working Days</Label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {DAYS_OF_WEEK.map(day => (
                      <div key={day.key} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`create-${day.key}`}
                            checked={newCalendar.working_days.find(wd => wd.day === day.key)?.is_working || false}
                            onCheckedChange={(checked) => {
                              setNewCalendar(prev => ({
                                ...prev,
                                working_days: prev.working_days.map(wd =>
                                  wd.day === day.key ? { ...wd, is_working: checked } : wd
                                ),
                              }));
                            }}
                          />
                          <Label htmlFor={`create-${day.key}`} className="text-sm">{day.label}</Label>
                        </div>
                        {newCalendar.working_days.find(wd => wd.day === day.key)?.is_working && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Input
                              type="time"
                              className="h-7 w-24 text-xs"
                              value={newCalendar.working_days.find(wd => wd.day === day.key)?.work_start || '08:00'}
                              onChange={(e) => {
                                setNewCalendar(prev => ({
                                  ...prev,
                                  working_days: prev.working_days.map(wd =>
                                    wd.day === day.key ? { ...wd, work_start: e.target.value } : wd
                                  ),
                                }));
                              }}
                            />
                            <span>to</span>
                            <Input
                              type="time"
                              className="h-7 w-24 text-xs"
                              value={newCalendar.working_days.find(wd => wd.day === day.key)?.work_end || '17:00'}
                              onChange={(e) => {
                                setNewCalendar(prev => ({
                                  ...prev,
                                  working_days: prev.working_days.map(wd =>
                                    wd.day === day.key ? { ...wd, work_end: e.target.value } : wd
                                  ),
                                }));
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter className="p-6 pt-2">
                <Button variant="ghost" onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateCalendar} disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Calendar'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
        {/* Calendar List */}
        <div className="sm:col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Available Calendars</CardTitle>
              <CardDescription className="text-xs">
                {calendars.length} calendar{calendars.length !== 1 ? 's' : ''} configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : calendars.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No calendars yet</p>
                  <p className="text-xs text-slate-400">Create one to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {calendars.map((cal) => (
                    <button
                      key={cal.id}
                      onClick={() => setSelectedCalendar(cal.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedCalendar === cal.id
                          ? 'bg-blue-50 border-blue-200'
                          : 'hover:bg-slate-50 border-slate-200'
                        }`}
                      data-testid={`calendar-item-${cal.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium text-sm ${selectedCalendar === cal.id ? 'text-blue-900' : 'text-slate-800'
                          }`}>
                          {cal.name}
                        </span>
                        {cal.is_default && (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                            <Star className="h-2.5 w-2.5 mr-1" />
                            Default
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Sun className="h-3 w-3" />
                          {cal.working_days_count} work days
                        </span>
                        <span className="flex items-center gap-1">
                          <CalendarX className="h-3 w-3" />
                          {cal.holidays_count} holidays
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calendar Details */}
        <div className="sm:col-span-8">
          {!selectedCalendar ? (
            <Card className="h-full flex items-center justify-center">
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Select a calendar to view details</p>
              </div>
            </Card>
          ) : loadingDetails ? (
            <Card className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </Card>
          ) : calendarDetails ? (
            <Card>
              <CardHeader className="border-b">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-lg">
                      {isEditing ? (
                        <Input
                          value={editingCalendar?.name || ''}
                          onChange={(e) => setEditingCalendar({ ...editingCalendar, name: e.target.value })}
                          className="h-8 text-lg font-semibold"
                        />
                      ) : (
                        calendarDetails.name
                      )}
                    </CardTitle>
                    {!isEditing && calendarDetails.description && (
                      <CardDescription className="text-xs mt-1">{calendarDetails.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {calendarDetails.is_default && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        <Star className="h-3 w-3 mr-1" />
                        Default
                      </Badge>
                    )}
                    {can(Action.MANAGE_SCHEDULES) && !isEditing && (
                      <>
                        {!calendarDetails.is_default && (
                          <Button variant="outline" size="sm" onClick={handleSetDefault}>
                            <Star className="h-3.5 w-3.5 mr-1" />
                            Set Default
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={handleStartEdit}>
                          <Edit2 className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        {!calendarDetails.is_default && (
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDeleteCalendar}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                    {isEditing && (
                      <>
                        <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveChanges} disabled={isSaving}>
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <Save className="h-4 w-4 mr-1" />
                          )}
                          Save
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-slate-500 mb-1">Hours/Day</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {isEditing ? (
                        <Input
                          type="number"
                          className="h-7 w-20 text-sm"
                          value={editingCalendar?.hours_per_day || 8}
                          onChange={(e) => setEditingCalendar({ ...editingCalendar, hours_per_day: parseFloat(e.target.value) || 8 })}
                        />
                      ) : (
                        calendarDetails.hours_per_day
                      )}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-slate-500 mb-1">Hours/Week</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {isEditing ? (
                        <Input
                          type="number"
                          className="h-7 w-20 text-sm"
                          value={editingCalendar?.hours_per_week || 40}
                          onChange={(e) => setEditingCalendar({ ...editingCalendar, hours_per_week: parseFloat(e.target.value) || 40 })}
                        />
                      ) : (
                        calendarDetails.hours_per_week
                      )}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-blue-600 mb-1">Work Days</p>
                    <p className="text-lg font-semibold text-blue-800">
                      {(isEditing ? editingCalendar?.working_days : calendarDetails.working_days)
                        ?.filter(wd => wd.is_working).length || 0} / 7
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-amber-600 mb-1">Holidays</p>
                    <p className="text-lg font-semibold text-amber-800">
                      {calendarDetails.holidays?.length || 0}
                    </p>
                  </div>
                </div>

                <Separator className="my-4" />

                {/* Working Days */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Working Days Schedule
                  </h3>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-8"></TableHead>
                          <TableHead className="text-xs">Day</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Start Time</TableHead>
                          <TableHead className="text-xs">End Time</TableHead>
                          <TableHead className="text-xs text-right">Hours</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(isEditing ? editingCalendar?.working_days : calendarDetails.working_days)?.map((wd) => {
                          const dayInfo = DAYS_OF_WEEK.find(d => d.key === wd.day);
                          return (
                            <TableRow key={wd.day} className={wd.is_working ? '' : 'bg-slate-50/50'}>
                              <TableCell className="py-2">
                                {isEditing ? (
                                  <Checkbox
                                    checked={wd.is_working}
                                    onCheckedChange={(checked) => updateWorkingDay(wd.day, 'is_working', checked)}
                                  />
                                ) : wd.is_working ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-slate-300" />
                                )}
                              </TableCell>
                              <TableCell className="text-sm font-medium py-2">{dayInfo?.label}</TableCell>
                              <TableCell className="py-2">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${wd.is_working
                                      ? 'bg-green-50 text-green-700 border-green-200'
                                      : 'bg-slate-100 text-slate-500 border-slate-200'
                                    }`}
                                >
                                  {wd.is_working ? 'Working' : 'Non-Working'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600 py-2">
                                {wd.is_working ? (
                                  isEditing ? (
                                    <Input
                                      type="time"
                                      className="h-7 w-24 text-xs"
                                      value={wd.work_start}
                                      onChange={(e) => updateWorkingDay(wd.day, 'work_start', e.target.value)}
                                    />
                                  ) : (
                                    wd.work_start
                                  )
                                ) : '—'}
                              </TableCell>
                              <TableCell className="text-sm text-slate-600 py-2">
                                {wd.is_working ? (
                                  isEditing ? (
                                    <Input
                                      type="time"
                                      className="h-7 w-24 text-xs"
                                      value={wd.work_end}
                                      onChange={(e) => updateWorkingDay(wd.day, 'work_end', e.target.value)}
                                    />
                                  ) : (
                                    wd.work_end
                                  )
                                ) : '—'}
                              </TableCell>
                              <TableCell className="text-sm text-right font-mono py-2">
                                {wd.is_working ? `${wd.hours_per_day}h` : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Holidays */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <CalendarX className="h-4 w-4" />
                      Holidays & Exceptions
                    </h3>
                    {can(Action.MANAGE_SCHEDULES) && !isEditing && (
                      <div className="flex items-center gap-2">
                        {/* Import Button */}
                        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1">
                              <Upload className="h-3.5 w-3.5" />
                              Import
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[500px]">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                                Import Holidays
                              </DialogTitle>
                              <DialogDescription>
                                Import holidays from a CSV or Excel file
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              {/* File Upload */}
                              <div className="space-y-2">
                                <Label>Select File (CSV)</Label>
                                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                  <input
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    id="holiday-file-input"
                                  />
                                  <label
                                    htmlFor="holiday-file-input"
                                    className="cursor-pointer"
                                  >
                                    <Upload className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                    <p className="text-sm text-slate-600 mb-1">
                                      {importFile ? importFile.name : 'Click to upload or drag & drop'}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      CSV file with columns: Date, Name
                                    </p>
                                  </label>
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="h-auto p-0 text-xs"
                                  onClick={handleDownloadSample}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Download sample CSV
                                </Button>
                              </div>

                              {/* Error */}
                              {importError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                  {importError}
                                </div>
                              )}

                              {/* Preview */}
                              {importPreview.length > 0 && (
                                <div className="space-y-2">
                                  <Label>Preview ({importPreview.length} holidays)</Label>
                                  <ScrollArea className="h-[200px] border rounded-lg">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="text-xs">Date</TableHead>
                                          <TableHead className="text-xs">Name</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {importPreview.map((h, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="text-xs">{h.date}</TableCell>
                                            <TableCell className="text-xs">{h.name}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </ScrollArea>
                                </div>
                              )}
                            </div>
                            <DialogFooter>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowImportDialog(false);
                                  setImportFile(null);
                                  setImportPreview([]);
                                  setImportError(null);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={handleImportHolidays}
                                disabled={isImporting || importPreview.length === 0}
                                className="gap-1"
                              >
                                {isImporting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Import {importPreview.length > 0 && `(${importPreview.length})`}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        {/* Add Single Holiday Button */}
                        <Dialog open={showAddHolidayDialog} onOpenChange={setShowAddHolidayDialog}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              Add Holiday
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[400px]">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <CalendarPlus className="h-5 w-5 text-amber-500" />
                                Add Holiday
                              </DialogTitle>
                              <DialogDescription>
                                Define a non-working day or exception
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Holiday Date *</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-full justify-start text-left font-normal"
                                    >
                                      <Calendar className="mr-2 h-4 w-4" />
                                      {newHoliday.date
                                        ? format(parseISO(newHoliday.date), 'PPP')
                                        : 'Pick a date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <CalendarComponent
                                      mode="single"
                                      selected={newHoliday.date ? parseISO(newHoliday.date) : undefined}
                                      onSelect={(date) => {
                                        if (date) {
                                          setNewHoliday({
                                            ...newHoliday,
                                            date: format(date, 'yyyy-MM-dd'),
                                          });
                                        }
                                      }}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <div className="space-y-2">
                                <Label>Holiday Name *</Label>
                                <Input
                                  placeholder="e.g., Christmas Day"
                                  value={newHoliday.name}
                                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="holiday-working"
                                  checked={newHoliday.is_working}
                                  onCheckedChange={(checked) => setNewHoliday({ ...newHoliday, is_working: checked })}
                                />
                                <Label htmlFor="holiday-working" className="text-sm">
                                  This is a working holiday (overtime)
                                </Label>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="ghost" onClick={() => setShowAddHolidayDialog(false)} disabled={isAddingHoliday}>
                                Cancel
                              </Button>
                              <Button onClick={handleAddHoliday} disabled={isAddingHoliday}>
                                {isAddingHoliday ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                  </>
                                ) : (
                                  'Add Holiday'
                                )}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </div>

                  {calendarDetails.holidays?.length === 0 ? (
                    <div className="border rounded-lg p-6 text-center bg-slate-50">
                      <CalendarX className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No holidays defined</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Add holidays to exclude them from scheduling calculations
                      </p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Name</TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            {can(Action.MANAGE_SCHEDULES) && !isEditing && <TableHead className="w-10"></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {calendarDetails.holidays?.map((holiday) => (
                            <TableRow key={holiday.date}>
                              <TableCell className="font-mono text-sm">
                                {format(parseISO(holiday.date), 'MMM d, yyyy')}
                              </TableCell>
                              <TableCell className="text-sm font-medium">{holiday.name}</TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${holiday.is_working
                                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                                      : 'bg-red-50 text-red-700 border-red-200'
                                    }`}
                                >
                                  {holiday.is_working ? 'Working Holiday' : 'Non-Working'}
                                </Badge>
                              </TableCell>
                              {can(Action.MANAGE_SCHEDULES) && !isEditing && (
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                    onClick={() => handleRemoveHoliday(holiday.date)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">How Calendars Work</p>
              <p className="text-xs text-blue-600 mt-1">
                Project calendars define working days, hours, and holidays. The CPM engine uses these settings
                to calculate activity dates, ensuring non-working days are excluded from duration calculations.
                Each project should have a default calendar that applies to all activities unless specified otherwise.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarsPage;
